import { z } from 'zod'
import { CODE_CHECKS, type CodeVerdict } from './checks'
import { deriveCertFacts, type CertInputContext, type CertSubject } from './facts'
import { CERT_JUDGE_ITEMS, type CertJudgeVerdict } from './judge'
import { CERT_RULES, type CertRule } from './rules'
import { CERT_RULES_VERSION, CERT_TARGETS, HALAL_MARKETS, type CertGoals } from './targets'
import { TERMS_COVERAGE } from './terms'

/**
 * 인증 준비도 (설계 3-1·3-2, 2026-11-01). 서버가 계산해 result.certReadiness 에 둔다 (앱이 보낸 값은 버린다).
 * 사람이 읽는 글이 없다 — 규칙 id·상태·재료 id·고정 문구 키만 있어서 번역이 필요 없고, AI 가 쓴 문장이 화면에 새지 않는다.
 *
 * 상태
 *  reflected ✅ 설계에 반영됨 — autoCheck '자동' 규칙이고 코드 검사가 지킨 것을 봤을 때만. AI 판정의 ok 는 ✅ 가 되지 않는다
 *            (AI 는 근거를 보여 줄 수 없다 — ⚠️ 로 남기고, AI 가 찾은 위반만 ❌ 로 쓴다)
 *  fix       ❌ 설계에서 고칠 것 — 코드나 AI 가 위반을 찾았고 심각도가 불합격·보완 (해결 문장은 substitutions.ts)
 *  docs      ⚠️ 공장·서류로 확인 — '공장 서류 필요' 규칙. 설계에 반영했어도 실제 분리·서류는 공장이 한다
 *  review    ⚠️ 전문가 확인 — '사람 확인' 규칙, AI 가 판정한 자동 규칙, '경고' 심각도 위반
 *  누가(who): ⚠️ 항목의 '설계' 는 전문가(review)·공장(docs)으로 바꿔 보인다 — 확인할 사람이 설계가 아니라서
 *  (na 는 저장하지 않고 개수만)
 *
 * 점수: 퍼센트·통과 확률·등급을 만들지 않는다 (심사 결과 자료가 없다). "앱이 점검한 a개 중 b개 반영" 과
 * "확인 필요 n개" 만 센다. 목표 여러 개를 한 숫자로 합치지 않는다.
 */
export const CERT_ITEM_STATUSES = ['reflected', 'fix', 'docs', 'review'] as const
export type CertItemStatus = (typeof CERT_ITEM_STATUSES)[number]
export const CERT_SEVERITIES = ['fail', 'improve', 'warn'] as const
export type CertSeverity = (typeof CERT_SEVERITIES)[number]
export const CERT_WHO = ['design', 'factory', 'importer', 'seller', 'expert'] as const
export const CERT_BY = ['code', 'ai', 'none'] as const

export const certItemSchema = z.object({
  ruleId: z.string().max(20),
  status: z.enum(CERT_ITEM_STATUSES),
  severity: z.enum(CERT_SEVERITIES),
  who: z.enum(CERT_WHO),
  by: z.enum(CERT_BY),
  /** 걸린 재료 id (이름은 앱이 지금 결과에서 찾는다 — 번역본에서도 같은 id) */
  ingredientIds: z.array(z.string().max(60)).max(25).optional(),
  /** 걸린 공정 단계 (0부터) */
  stepIndex: z.number().int().min(0).max(4).optional(),
  /** substitutions.ts 의 키 (해결 문장) */
  hintKey: z.string().max(40).optional(),
  /** 같은 규칙의 다른 설명 (예: HK-SCOPE-01 의 mandatory·voluntary) */
  variant: z.string().max(20).optional(),
  /** 재료·공정을 고친 뒤라 AI 판정을 다시 해야 함 */
  stale: z.boolean().optional(),
})
export type CertItem = z.infer<typeof certItemSchema>

const count = z.number().int().min(0).max(200)
export const certReadinessTargetSchema = z.object({
  id: z.enum(CERT_TARGETS),
  halalMarket: z.enum(HALAL_MARKETS).optional(),
  counts: z.object({
    reflected: count,
    fix: count,
    /** fix 중 불합격 심각도 (머리 문장이 '고칠 것' 이 된다) */
    fixFail: count,
    docs: count,
    review: count,
    na: count,
    /** 앱이 점검한 항목 (자동 규칙 중 ✅ 또는 ❌ 로 끝난 것) */
    checked: count,
  }),
  items: z.array(certItemSchema).max(90),
})
export type CertReadinessTarget = z.infer<typeof certReadinessTargetSchema>

export const certReadinessSchema = z.object({
  version: z.string().max(30),
  checkedAt: z.string().max(40),
  targets: z.array(certReadinessTargetSchema).max(4),
})
export type CertReadiness = z.infer<typeof certReadinessSchema>

/** 관리자 화면용 근거 한 줄 (simulations.internal.certEvidence). 앱 응답에는 없다 */
export const certEvidenceSchema = z.object({
  target: z.enum(CERT_TARGETS),
  ruleId: z.string().max(20),
  by: z.enum(['code', 'ai']),
  verdict: z.string().max(20),
  /** 코드가 걸린 말 (예: "맛술 ← 맛술", "sorbate 1.4 g/kg") */
  evidence: z.string().max(200).optional(),
  /** AI 심사의 이유 (80자 안팎) */
  why: z.string().max(200).optional(),
})
export type CertEvidence = z.infer<typeof certEvidenceSchema>

const SEVERITY: Record<CertRule['severity'], CertSeverity> = {
  불합격: 'fail',
  보완: 'improve',
  경고: 'warn',
}

/** 원료 사전으로 판정하는 규칙 — 그 언어 사전이 일부뿐이면(TERMS_COVERAGE) 걸린 것이 없어도 ✅ 대신 ⚠️ */
const DICTIONARY_RULES = new Set([
  'H-ING-01',
  'H-ING-02',
  'H-ING-03',
  'H-ING-04',
  'H-MKT-03',
  'H-SRC-08',
  'H-LBL-01',
  'HK-STD-01',
  'HK-STD-02',
  'HK-STD-04',
  'HK-STD-06',
  'HK-STD-07',
  'HK-STD-08',
  'HK-CCP-05',
  'HK-LBL-01',
  'HK-LBL-02',
])

/** 앱이 판정하지 못할 때의 ⚠️ (공장 서류 규칙은 공장, 나머지는 전문가) */
function fallbackStatus(rule: CertRule): 'docs' | 'review' {
  if (rule.autoCheck === '공장 서류 필요') return 'docs'
  if (rule.autoCheck === '사람 확인') return 'review'
  return rule.who === 'factory' || rule.who === 'importer' ? 'docs' : 'review'
}

function baseItem(rule: CertRule, by: CertItem['by']): Omit<CertItem, 'status'> {
  return { ruleId: rule.id, severity: SEVERITY[rule.severity], who: rule.who, by }
}

/** 판정 → 상태. ✅ 는 자동 규칙만 */
function statusOf(
  rule: CertRule,
  v: 'ok' | 'fix' | 'docs' | 'review' | 'unknown',
  partial: boolean,
) {
  if (v === 'ok') {
    if (rule.autoCheck !== '자동') return fallbackStatus(rule)
    return partial ? 'review' : 'reflected'
  }
  if (v === 'fix') return rule.severity === '경고' ? 'review' : 'fix'
  if (v === 'unknown') return fallbackStatus(rule)
  return v
}

/** ⚠️ 항목의 누가: '설계' 는 확인할 사람이 아니다 — 전문가 확인은 전문가, 서류는 공장 */
function whoFor(status: CertItemStatus, who: CertItem['who']): CertItem['who'] {
  if (who !== 'design') return who
  return status === 'review' ? 'expert' : status === 'docs' ? 'factory' : who
}

function itemFromCode(rule: CertRule, verdict: CodeVerdict, partial: boolean): CertItem {
  const status = statusOf(rule, verdict.v === 'na' ? 'unknown' : verdict.v, partial)
  const item: CertItem = { ...baseItem(rule, 'code'), status }
  if ('who' in verdict && verdict.who) item.who = verdict.who
  item.who = whoFor(status, item.who)
  if ('ingredientIds' in verdict && verdict.ingredientIds?.length)
    item.ingredientIds = verdict.ingredientIds.slice(0, 25)
  if ('stepIndex' in verdict && verdict.stepIndex !== undefined && verdict.stepIndex <= 4)
    item.stepIndex = verdict.stepIndex
  if ('hintKey' in verdict && verdict.hintKey) item.hintKey = verdict.hintKey
  if (verdict.variant) item.variant = verdict.variant
  return item
}

/** AI 의 ref (재료 id 또는 1부터 센 step 번호) → 항목 칸. step 은 '3'·'step 3' 만 ('salt-2' 같은 모르는 재료 id 는 공정이 아니다) */
function refFields(ref: string | undefined, subject: CertSubject): Partial<CertItem> {
  if (!ref) return {}
  const ing = subject.ingredients.find((i) => i.id === ref.trim())
  if (ing) return { ingredientIds: [ing.id] }
  const step = ref.trim().match(/^(?:step\s*)?(\d+)$/iu)
  const n = step ? Number(step[1]) : NaN
  return Number.isInteger(n) && n >= 1 && n <= Math.min(5, subject.steps.length)
    ? { stepIndex: n - 1 }
    : {}
}

/**
 * AI 판정 → 항목. AI 가 찾은 위반(fix)만 ❌(경고 규칙은 ⚠️)이고, ok·unknown·판정 없음은 그 규칙의 기본 ⚠️ 다.
 * AI 의 ok 는 ✅ 가 되지 않는다 — 그래서 judge.ts 의 fixOnly 는 이제 심사 질문 쪽 뜻만 남는다
 */
function itemFromAi(
  rule: CertRule,
  verdict: CertJudgeVerdict | undefined,
  subject: CertSubject,
): CertItem {
  const status: CertItemStatus =
    verdict?.v === 'fix' ? statusOf(rule, 'fix', false) : fallbackStatus(rule)
  return {
    ...baseItem(rule, 'ai'),
    status,
    who: whoFor(status, rule.who),
    ...(verdict?.v === 'fix' ? refFields(verdict.ref, subject) : {}),
  }
}

/**
 * 고친 뒤 이어 가는 AI 항목: 판정이 지금 설계와 맞는지 모르니 ⚠️ 로 낮추고 stale 을 단다.
 * 걸린 재료·공정 칸은 뺀다 (고친 설계에서 그 재료·단계가 바뀌었을 수 있다)
 */
function staleAiItem(prev: CertItem, rule: CertRule): CertItem {
  const { ingredientIds: _ids, stepIndex: _step, ...rest } = prev
  const status = fallbackStatus(rule)
  return { ...rest, status, who: whoFor(status, rule.who), stale: true }
}

export interface ComputeCertReadinessOptions {
  /** 생성 요청의 분류 (input.majorId·subId) */
  input?: CertInputContext
  /** 이번 심사의 AI 판정. 생성 때만 있다. null 이면 심사가 없었다 (AI 항목은 ⚠️) */
  aiVerdicts?: CertJudgeVerdict[] | null
  /** 저장 재계산: 지난 준비도 (AI 항목은 지난 값을 두고 stale 을 단다) */
  previous?: CertReadiness | null
  /** 점검 뒤 배합·공정을 고쳤는지 */
  edited?: boolean
  now?: Date
}

/** 이미 한 번 고친 적이 있는 준비도인지 (다시 확인 표시를 이어 간다) */
function wasEdited(previous: CertReadiness | null | undefined): boolean {
  return (
    previous?.targets.some((t) =>
      t.items.some((i) => i.stale || i.ruleId === 'H-DOC-03' || i.ruleId === 'HK-PLAN-12'),
    ) ?? false
  )
}

/** 결과 하나의 준비도와 관리자 근거를 계산한다. 목표가 없으면 null */
export function computeCertReadiness(
  subject: CertSubject,
  goals: CertGoals | null,
  options: ComputeCertReadinessOptions = {},
): { readiness: CertReadiness; evidence: CertEvidence[] } | null {
  if (!goals || goals.targets.length === 0) return null
  const edited = options.edited === true || wasEdited(options.previous)
  const facts = deriveCertFacts(subject, goals, { input: options.input, edited })
  const partial = TERMS_COVERAGE[subject.language] === 'partial'
  const evidence: CertEvidence[] = []
  const targets: CertReadinessTarget[] = []
  for (const target of goals.targets) {
    if (target !== 'halal' && target !== 'haccp') continue
    const items: CertItem[] = []
    let na = 0
    const previousItems = options.previous?.targets.find((t) => t.id === target)?.items ?? []
    for (const rule of CERT_RULES[target]) {
      if (rule.when && !rule.when(facts)) {
        na++
        continue
      }
      const ruleItems: CertItem[] = []
      // 1) 코드 검사. 모두 해당 없음이면 (예: 젤라틴이 없으면 H-SRC-01) AI 가 고칠 것을 찾지 않는 한 해당 없음
      let codeNa = false
      if (rule.engine === 'code') {
        const verdicts = CODE_CHECKS[rule.id]?.(facts) ?? [{ v: 'unknown' as const }]
        codeNa = verdicts.every((v) => v.v === 'na')
        for (const verdict of verdicts.filter((v) => v.v !== 'na')) {
          ruleItems.push(itemFromCode(rule, verdict, partial && DICTIONARY_RULES.has(rule.id)))
          evidence.push({
            target,
            ruleId: rule.id,
            by: 'code',
            verdict: verdict.v,
            ...(verdict.evidence ? { evidence: verdict.evidence.slice(0, 200) } : {}),
          })
        }
      }
      // 2) AI 판정. 저장 재계산(aiVerdicts 없음)이면 지난 AI 항목을 그대로 두고, 고친 뒤면 stale
      const judgeItem = CERT_JUDGE_ITEMS.find((j) => j.ruleId === rule.id)
      if (rule.engine === 'ai' || (rule.aiAlso && judgeItem)) {
        const fromPrevious = options.aiVerdicts === undefined
        const verdict = options.aiVerdicts?.find((v) => v.id === judgeItem?.id)
        if (verdict)
          evidence.push({
            target,
            ruleId: rule.id,
            by: 'ai',
            verdict: verdict.v,
            ...(verdict.why ? { why: verdict.why } : {}),
          })
        const prev = previousItems.find((i) => i.ruleId === rule.id && i.by === 'ai')
        const carried = fromPrevious && prev ? (edited ? staleAiItem(prev, rule) : prev) : null
        if (rule.engine === 'ai') {
          ruleItems.push(carried ?? itemFromAi(rule, verdict, subject))
        } else {
          // 코드 검사 위에 AI 가 고칠 것을 찾았으면 그것도 남긴다 (코드가 이미 ❌ 면 더하지 않고, ✅ 는 거둔다).
          // 고친 뒤 이어 온 항목(⚠️ stale)도 ✅ 를 거둔다 — AI 가 찾았던 문제가 아직 남았는지 모른다
          const aiItem =
            carried ?? (verdict?.v === 'fix' ? itemFromAi(rule, verdict, subject) : null)
          if (aiItem && !ruleItems.some((i) => i.status === 'fix')) {
            for (let k = ruleItems.length - 1; k >= 0; k--)
              if (ruleItems[k]!.status === 'reflected') ruleItems.splice(k, 1)
            ruleItems.push(aiItem)
          }
        }
      }
      // 3) 앱이 못 보는 규칙 (공장·서류·심사)
      if (rule.engine === 'none')
        ruleItems.push({ ...baseItem(rule, 'none'), status: fallbackStatus(rule) })
      if (ruleItems.length === 0 && codeNa) {
        na++
        continue
      }
      items.push(...ruleItems)
    }
    const kept = items.slice(0, 90)
    // '앱이 점검한' 수는 코드 검사가 판정한 자동 규칙만 센다 (AI 판정은 앱이 근거를 보여 줄 수 없다)
    const autoRule = (i: CertItem) =>
      i.by === 'code' && CERT_RULES[target].find((r) => r.id === i.ruleId)?.autoCheck === '자동'
    targets.push({
      id: target,
      ...(target === 'halal' ? { halalMarket: facts.halalMarket } : {}),
      counts: {
        reflected: kept.filter((i) => i.status === 'reflected').length,
        fix: kept.filter((i) => i.status === 'fix').length,
        fixFail: kept.filter((i) => i.status === 'fix' && i.severity === 'fail').length,
        docs: kept.filter((i) => i.status === 'docs').length,
        review: kept.filter((i) => i.status === 'review').length,
        na,
        checked: kept.filter((i) => autoRule(i) && (i.status === 'reflected' || i.status === 'fix'))
          .length,
      },
      items: kept,
    })
  }
  if (targets.length === 0) return null
  return {
    readiness: {
      version: CERT_RULES_VERSION,
      checkedAt: (options.now ?? new Date()).toISOString(),
      targets,
    },
    evidence,
  }
}

/** 저장 재계산 (/apply·AI 수정): 코드 검사만 다시, AI 항목은 지난 값 + stale. 바뀐 것이 없으면 지난 값 그대로 (저장 지문이 그대로) */
export function recheckCertReadiness(
  previous: CertReadiness | null | undefined,
  subject: CertSubject,
  goals: CertGoals | null,
  options: { input?: CertInputContext; edited: boolean; now?: Date },
): CertReadiness | null {
  const next = computeCertReadiness(subject, goals, { ...options, previous: previous ?? null })
  if (!next) return null
  if (previous && JSON.stringify(previous.targets) === JSON.stringify(next.readiness.targets))
    return previous
  return next.readiness
}

/** 재료·공정·문구가 바뀌었는지 (AI 판정을 다시 해야 하는지). 숫자만 바뀐 원가 수정은 아니다 */
export function certDesignChanged(before: CertSubject, after: CertSubject): boolean {
  const design = (s: CertSubject) =>
    JSON.stringify([
      s.product.name,
      s.product.tagline,
      s.product.subtype,
      s.product.format,
      s.product.storage,
      s.product.shelfLifeDays,
      s.ingredients.map((i) => [i.id, i.name, i.qty, i.unit, i.substitutes.map((x) => x.name)]),
      s.steps,
      s.estPh,
      s.complianceSummary,
      s.baseNote,
      s.caution,
    ])
  return design(before) !== design(after)
}

/** 목표 한 줄의 머리 문장 종류 (설계 3-2): ❌ 없음 / ❌ 가 보완뿐 / ❌ 에 불합격 */
export function readinessHeadline(t: CertReadinessTarget): {
  kind: 'ok' | 'improve' | 'fix'
  fixCount: number
  confirmCount: number
  checked: number
  reflected: number
} {
  const kind = t.counts.fix === 0 ? 'ok' : t.counts.fixFail > 0 ? 'fix' : 'improve'
  return {
    kind,
    fixCount: t.counts.fix,
    confirmCount: t.counts.docs + t.counts.review,
    checked: t.counts.checked,
    reflected: t.counts.reflected,
  }
}

/** 재료·공정을 고쳐 다시 확인할 항목이 있는지 */
export function readinessStale(r: CertReadiness | null | undefined): boolean {
  return r?.targets.some((t) => t.items.some((i) => i.stale)) ?? false
}

/** 게이트용: 생성 모드에서 다시 만들게 할 '불합격' 코드 위반 (설계 3-7). [규칙 id, 재료 id들, 대체안 키] */
export function certHardViolations(
  subject: CertSubject,
  goals: CertGoals | null,
  input?: CertInputContext,
): { ruleId: string; ingredientIds: string[]; hintKey?: string; evidence?: string }[] {
  if (!goals || goals.targets.length === 0 || subject.mode === 'analyze') return []
  const facts = deriveCertFacts(subject, goals, { input })
  const out: { ruleId: string; ingredientIds: string[]; hintKey?: string; evidence?: string }[] = []
  for (const target of goals.targets) {
    if (target !== 'halal' && target !== 'haccp') continue
    for (const rule of CERT_RULES[target]) {
      if (!rule.hard || rule.engine !== 'code' || (rule.when && !rule.when(facts))) continue
      for (const verdict of CODE_CHECKS[rule.id]?.(facts) ?? []) {
        if (verdict.v !== 'fix' || verdict.noRegenerate) continue
        out.push({
          ruleId: rule.id,
          ingredientIds: verdict.ingredientIds ?? [],
          ...(verdict.hintKey ? { hintKey: verdict.hintKey } : {}),
          ...(verdict.evidence ? { evidence: verdict.evidence } : {}),
        })
      }
    }
  }
  return out
}
